import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Global error suppression for Google Translate errors - MUST run before React loads
(function() {
  'use strict';
  
  // Helper to check if error is from Google Translate
  const isGoogleTranslateError = (msg, url, error) => {
    const msgStr = String(msg || '');
    const urlStr = String(url || '');
    const errorStack = error?.stack || '';
    
    const patterns = [
      'translate.googleapis.com',
      'translate_http',
      'Script error.',
      '_.k.Yh',
      '_.k.ja',
      '_.k.ph',
      '_.k.uf',
      'Yn.uf',
      'TranslateElement',
      'is not a constructor',
      'translate'
    ];
    
    const combinedStr = msgStr + ' ' + urlStr + ' ' + errorStack;
    
    return patterns.some(pattern => 
      msgStr.includes(pattern) || 
      urlStr.includes(pattern) || 
      errorStack.includes(pattern) ||
      combinedStr.includes(pattern)
    );
  };

  // Override window.onerror BEFORE React loads
  const originalOnError = window.onerror;
  window.onerror = function(msg, url, lineNo, columnNo, error) {
    if (isGoogleTranslateError(msg, url, error)) {
      return true; // Suppress the error
    }
    if (originalOnError) {
      return originalOnError(msg, url, lineNo, columnNo, error);
    }
    return false;
  };

  // Override console.error to prevent React error overlay
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // Check all arguments for Google Translate patterns
    let shouldSuppress = false;
    let combinedStr = '';
    
    for (const arg of args) {
      let errorStr = '';
      if (typeof arg === 'string') {
        errorStr = arg;
      } else if (arg && typeof arg === 'object') {
        errorStr = [
          arg.message,
          arg.stack,
          arg.toString ? arg.toString() : '',
          JSON.stringify(arg)
        ].filter(Boolean).join(' ');
      } else {
        errorStr = String(arg);
      }
      
      combinedStr += ' ' + errorStr;
      
      if (isGoogleTranslateError(errorStr, '', null)) {
        shouldSuppress = true;
        break;
      }
    }
    
    // Also check combined string for patterns that might span multiple arguments
    if (!shouldSuppress && isGoogleTranslateError(combinedStr, '', null)) {
      shouldSuppress = true;
    }
    
    if (shouldSuppress) {
      return; // Suppress Google Translate errors
    }
    
    originalConsoleError.apply(console, args);
  };

  // Handle unhandled promise rejections
  const originalUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = function(event) {
    if (event && event.reason) {
      const reason = String(event.reason);
      const reasonStack = event.reason?.stack || '';
      if (isGoogleTranslateError(reason, '', { stack: reasonStack })) {
        event.preventDefault();
        return;
      }
    }
    if (originalUnhandledRejection) {
      return originalUnhandledRejection(event);
    }
  };

  // Intercept React's error overlay handler
  window.addEventListener('error', function(event) {
    if (isGoogleTranslateError(event.message, event.filename, event.error)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }, true); // Use capture phase

  // Also intercept unhandledrejection events
  window.addEventListener('unhandledrejection', function(event) {
    if (event && event.reason) {
      const reason = String(event.reason);
      const reasonStack = event.reason?.stack || '';
      if (isGoogleTranslateError(reason, '', { stack: reasonStack })) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    }
  }, true);

  // Intercept React DevTools error overlay if it exists
  // This runs after a delay to catch the overlay after it's initialized
  setTimeout(() => {
    // Try to override React's error overlay handler
    if (window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__) {
      const originalHandler = window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
      window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = function(error, isFatal) {
        const errorMsg = error?.message || error?.toString() || '';
        const errorStack = error?.stack || '';
        if (isGoogleTranslateError(errorMsg, '', { stack: errorStack })) {
          return; // Suppress
        }
        return originalHandler(error, isFatal);
      };
    }

    // Also try to find and override handleError in the bundle
    // This is a more aggressive approach
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
      if (hook.onCommitFiberRoot) {
        const originalOnCommitFiberRoot = hook.onCommitFiberRoot;
        hook.onCommitFiberRoot = function(...args) {
          try {
            return originalOnCommitFiberRoot.apply(this, args);
          } catch (error) {
            if (!isGoogleTranslateError(error?.message, '', error)) {
              throw error;
            }
          }
        };
      }
    }
    
    // Try to intercept React's internal error handling
    // Override React's error reporting at a lower level
    const originalCreateElement = React.createElement;
    if (originalCreateElement) {
      // This is a fallback - React errors are caught by ErrorBoundary
    }
  }, 100);
})();

const container = document.getElementById('root');
const root = createRoot(container);

// Replace with your actual Google Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '976908974547-gbsftrnkjj0qcf3nhlutc5nffhcv2gbg.apps.googleusercontent.com';

root.render(
  // <React.StrictMode>
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  // </React.StrictMode>,
);

serviceWorkerRegistration.register();
